import { MainArea, Modal, NotificationVariation, Page, Text, Theme } from '@salecycle/ui-component-library';
import { Actions as NotificationBarActions } from 'core/ui/notificationBar';
import { RulesetSplitTest } from 'entities/rulesetSplitTest';
import * as React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import arrayMove from 'array-move';
import { TFunction, useTranslation } from 'react-i18next';
import { Actions as RouterActions, Selectors as Router } from 'router';
import { Actions as TemplateActions } from 'sections/assets/state/templates';
import { Actions as CampaignActions, Selectors } from 'sections/campaigns/state/campaigns';
import { Selectors as CampaignSelectors } from 'sections/campaigns/state/campaigns';
import { Selectors as EditorSelectors } from 'sections/campaigns/state/editor';
import { Actions as GlobalWhitelistActions } from 'sections/campaigns/state/globalIpWhitelist';
import { Actions as WhitelistActions } from 'sections/campaigns/state/ipWhitelist';
import { Actions as RulesetActions, Selectors as RulesetSelectors } from 'sections/campaigns/state/rulesets';
import { Selectors as RulesetSplitTestSelectors } from 'sections/campaigns/state/rulesetSplitTests';
import { State } from 'store';
import AppLoader from 'core/ui/components/AppLoader';
import { Selectors as StepSelectors } from '../../state/editor';
import { Sidebar } from './components';
import Toolbar from './components/ButtonToolbar';
import { Campaign } from 'entities/campaign';
import RulesetList from './components/RulesetList';
import { getProgress } from './progress';
import { State as CampaignState } from '../../../../entities/lifecycle/lifeCycle';
import { getCampaignSubState } from 'entities/lifecycle/activityState';
import Site from 'sections/client-management/entities/site';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';

export const getStatusColor = (campaignState: CampaignState, theme: Theme) => {
  switch (campaignState) {
    case 'ACTIVE':
      return theme.vars.newColor.green2;
    case 'TESTING':
      return theme.vars.newColor.orange2;
    case 'MIGRATION':
      return theme.vars.newColor.blue1;
    case 'INACTIVE':
      return theme.vars.newColor.lightGS1;
    case 'DELETED':
      return theme.vars.newColor.darkGS2;
    case 'SCHEDULED':
      return theme.vars.newColor.blue3;
    case 'FINISHED':
      return theme.vars.newColor.purple2;
    default:
      return null;
  }
};

const StyledEditor = styled.div`
  position: fixed;
  z-index: 10;
  background-color: #fff;
  width: 100%;
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  height: 100%;
  overflow-x: hidden;
`;

interface StateProps {
  updatedCampaign: Campaign;
  siteId?: string;
  campaignId: string;
  isLoading: boolean;
  rulesetIds: string[];
  showSetupFirstRuleset: boolean;
  activeSplitTestsForCampaign: RulesetSplitTest[];
  hasUnsavedChanges: boolean;
  hasMissingFields: (t: TFunction<'campaigns'>) => boolean;
  site: Site;
}

interface DispatchProps {
  fetchCampaign: (id: string) => void;
  fetchRulesets: () => void;
  fetchTemplates: () => void;
  fetchGlobalWhitelist: () => void;
  fetchWhitelist: (campaignId: string) => void;
  push: (href: string) => void;
  showNotification: (variation: NotificationVariation, detail: string) => void;
  update: (campaignId: string, campaign: Campaign) => void;
}

export type Props = StateProps & DispatchProps;

export function Editor(props: Props) {
  const { t } = useTranslation('campaigns');
  const { fetchCampaign, fetchRulesets, fetchTemplates, fetchGlobalWhitelist, fetchWhitelist } = props;
  const { campaignId } = props;
  const [hasDismissedSplitTestModal, setHasDismissedSplitTestModal] = React.useState(false);

  React.useEffect(() => {
    fetchCampaign(campaignId);
    fetchGlobalWhitelist();
    fetchWhitelist(campaignId);
    fetchRulesets();
    fetchTemplates();
  }, [campaignId, fetchCampaign, fetchGlobalWhitelist, fetchRulesets, fetchTemplates, fetchWhitelist]);

  const okButtonClicked = () => setHasDismissedSplitTestModal(true);

  const onSortEnd = ({ oldIndex, newIndex }) =>
    props.update(props.campaignId, { rulesetPriorities: arrayMove(props.rulesetIds, oldIndex, newIndex) });

  if (props.isLoading) {
    return (
      <StyledEditor>
        <AppLoader showGradient={true} />
      </StyledEditor>
    );
  } else {
    const hasMissingFields = props.hasMissingFields(t);
    return (
      <>
        {props.activeSplitTestsForCampaign.length > 0 && (
          <Modal
            isOpen={!hasDismissedSplitTestModal}
            variation="clean"
            size="medium"
            heading="Split Test Active"
            onOkButtonClicked={okButtonClicked}
          >
            <Text>This campaign has following active split test(s)</Text>
            <ul>
              {props.activeSplitTestsForCampaign.map((splitTest, index) => (
                <li key={`splittest-${index}`}>{splitTest.name}</li>
              ))}
            </ul>
            <Text>
              No changes can be made to a campaign whilst it has active split test(s). If changes are required to this
              campaign, please end the split test(s).
            </Text>
          </Modal>
        )}
        <StyledEditor>
          <MainArea>
            <Page padding={0}>
              <StyledGrid>
                <Sidebar
                  hasMissingFields={hasMissingFields}
                  hasUnsavedChanges={props.hasUnsavedChanges}
                  siteId={props.siteId}
                />
                <div>
                  <RulesetList
                    rulesetIds={props.rulesetIds}
                    onSortEnd={onSortEnd}
                    campaignState={getCampaignSubState(props.updatedCampaign)}
                  />
                </div>
              </StyledGrid>
            </Page>
            <Toolbar
              hasMissingFields={hasMissingFields}
              hasUnsavedChanges={props.hasUnsavedChanges}
              saveButtonPermanentlyDisabled={props.activeSplitTestsForCampaign.length > 0}
            />
          </MainArea>
        </StyledEditor>
      </>
    );
  }
}

const mapStateToProps = (campaignState: State): StateProps => {
  const campaignId = Router.getCampaignId(campaignState);
  const campaign = Selectors.getEntity(campaignState, campaignId);
  const updatedCampaign = CampaignSelectors.getUpdatedEntity(campaignState, campaignId);
  const siteId = Router.getSiteId(campaignState);
  const rulesetIds = StepSelectors.getRulesetIds(campaignState);
  const campaignHasChanges = CampaignSelectors.hasChanges(campaignState, campaignId);
  const hasRulesetsToDelete = !!EditorSelectors.getDeletedRulesetIds(campaignState).length;
  const site = SiteSelectors.getUpdatedEntity(campaignState, siteId);

  return {
    updatedCampaign,
    siteId,
    campaignId,
    isLoading: Selectors.isRefreshing(campaignState) || RulesetSelectors.isRefreshing(campaignState) || !campaign,
    rulesetIds,
    showSetupFirstRuleset: Router.getQuery(campaignState, 'showMessage') === 'true',
    activeSplitTestsForCampaign: RulesetSplitTestSelectors.getActiveSplitTestsForCampaign(campaignState, campaignId),
    hasUnsavedChanges:
      campaignHasChanges ||
      hasRulesetsToDelete ||
      !!rulesetIds.find(
        id => RulesetSelectors.hasChanges(campaignState, id) || RulesetSelectors.isNew(campaignState, id),
      ),
    hasMissingFields: t => !!rulesetIds.find(id => getProgress(campaignState, id, site.sessionTime, t).percent < 100),
    site,
  };
};

const actionCreators: DispatchProps = {
  fetchCampaign: (id: string) => CampaignActions.requestGet(id),
  fetchRulesets: () => RulesetActions.requestGetCollection({ clear: true }),
  fetchTemplates: () => TemplateActions.requestGetCollection({ clear: true, query: { type: 'onsite' } }),
  fetchGlobalWhitelist: () => GlobalWhitelistActions.requestGetCollection(),
  fetchWhitelist: (campaignId: string) => WhitelistActions.requestGetCollection({ params: { campaignId } }),
  push: RouterActions.push,
  showNotification: NotificationBarActions.customNotification,
  update: CampaignActions.updateLocal,
};

export default connect(mapStateToProps, actionCreators)(Editor);
